// Companies grouped for the workflow – used both in company selection
// and derived slugs for SEO landing pages.
export const COMPANY_GROUPS = [
  {
    id: "telecom",
    label: "İnternet / Telefon / TV",
    items: [
      "Vodafone Net",
      "Turkcell Superonline",
      "Türk Telekom",
      "D-Smart",
      "Digiturk / beIN",
      "Millenicom",
      "TurkNet",
      "KabloNet",
      "Kablo TV",
      "Tivibu",
    ],
  },
  {
    id: "mobile",
    label: "Mobil Operatörler",
    items: ["Turkcell", "Vodafone", "Türk Telekom Mobil"],
  },
  {
    id: "utility",
    label: "Elektrik / Doğalgaz / Su",
    items: ["Elektrik aboneliği", "Doğalgaz aboneliği", "Su aboneliği"],
  },
  {
    id: "digital",
    label: "Dijital Üyelikler",
    items: [
      "Netflix",
      "Exxen",
      "BluTV",
      "TOD",
      "YouTube Premium",
      "Spotify Premium",
      "Google Play",
      "Apple App Store",
      "Amazon Prime",
    ],
  },
  {
    id: "ecommerce",
    label: "E-ticaret",
    items: [
      "Trendyol",
      "Hepsiburada",
      "Amazon Türkiye",
      "n11",
      "PttAVM",
      "ÇiçekSepeti",
    ],
  },
  {
    id: "other",
    label: "Diğer",
    items: [
      "Spor salonu",
      "Online eğitim / kurs",
      "Sigorta / ek paket",
      "Diğer",
    ],
  },
];

export const COMPANY_INSTITUTION_BINDINGS = Object.freeze({
  "Vodafone Net": Object.freeze({ companyId: "vodafone-net", serviceScope: "evde-internet", category: "telecom" }),
  "Turkcell Superonline": Object.freeze({ companyId: "turkcell-superonline", serviceScope: "evde-internet", category: "telecom" }),
  "Türk Telekom": Object.freeze({ companyId: "turk-telekom", serviceScope: "evde-internet", category: "telecom" }),
  "D-Smart": Object.freeze({ companyId: "d-smart", serviceScope: "tv", category: "telecom" }),
  "Digiturk / beIN": Object.freeze({ companyId: "digiturk", serviceScope: "tv", category: "telecom" }),
  Millenicom: Object.freeze({ companyId: "millenicom", serviceScope: "evde-internet", category: "telecom" }),
  TurkNet: Object.freeze({ companyId: "turknet", serviceScope: "evde-internet", category: "telecom" }),
  Tivibu: Object.freeze({ companyId: "turk-telekom", serviceScope: "tivibu", category: "telecom" }),
  KabloNet: Object.freeze({
    companyId: "turksat-kablo",
    serviceScope: "kablonet",
    category: "telecom",
  }),
  "Kablo TV": Object.freeze({
    companyId: "turksat-kablo",
    serviceScope: "kablo-tv",
    category: "telecom",
  }),
  Turkcell: Object.freeze({
    companyId: "turkcell-mobile",
    serviceScope: "mobile",
    category: "mobile",
  }),
  Vodafone: Object.freeze({
    companyId: "vodafone-mobile",
    serviceScope: "mobile",
    category: "mobile",
  }),
  "Türk Telekom Mobil": Object.freeze({
    companyId: "turk-telekom-mobile",
    serviceScope: "mobile",
    category: "mobile",
  }),
  Netflix: Object.freeze({ companyId: "netflix", serviceScope: "streaming", category: "digital" }),
  Exxen: Object.freeze({ companyId: "exxen", serviceScope: "streaming", category: "digital" }),
  BluTV: Object.freeze({ companyId: "max", serviceScope: "streaming", category: "digital" }),
  TOD: Object.freeze({ companyId: "tod", serviceScope: "streaming", category: "digital" }),
  "YouTube Premium": Object.freeze({ companyId: "youtube-premium", serviceScope: "premium-membership", category: "digital" }),
  "Spotify Premium": Object.freeze({ companyId: "spotify-premium", serviceScope: "music", category: "digital" }),
  "Google Play": Object.freeze({ companyId: "google-play", serviceScope: "app-marketplace", category: "digital" }),
  "Apple App Store": Object.freeze({ companyId: "apple-app-store", serviceScope: "app-marketplace", category: "digital" }),
  "Amazon Prime": Object.freeze({ companyId: "amazon-prime", serviceScope: "membership", category: "digital" }),
  Trendyol: Object.freeze({ companyId: "trendyol", serviceScope: "marketplace", category: "ecommerce" }),
  Hepsiburada: Object.freeze({ companyId: "hepsiburada", serviceScope: "marketplace", category: "ecommerce" }),
  "Amazon Türkiye": Object.freeze({ companyId: "amazon-turkiye", serviceScope: "marketplace", category: "ecommerce" }),
  n11: Object.freeze({ companyId: "n11", serviceScope: "marketplace", category: "ecommerce" }),
  PttAVM: Object.freeze({ companyId: "pttavm", serviceScope: "marketplace", category: "ecommerce" }),
  ÇiçekSepeti: Object.freeze({ companyId: "ciceksepeti", serviceScope: "marketplace", category: "ecommerce" }),
});

export function getCompanyInstitutionContext(companyName) {
  return COMPANY_INSTITUTION_BINDINGS[companyName] || null;
}

export const ALL_COMPANIES = COMPANY_GROUPS.flatMap((g) =>
  g.items.map((name) => ({
    name,
    group: g.id,
    ...(getCompanyInstitutionContext(name) || {}),
  }))
);

// Presentation-only shortcuts. The company values sent to the backend remain unchanged.
export const COMPANY_CATEGORIES = [
  { id: "internet", label: "İnternet", groups: ["telecom"], exclude: ["D-Smart", "Digiturk / beIN", "Kablo TV", "Tivibu"] },
  { id: "tv", label: "TV / Yayın", names: ["D-Smart", "Digiturk / beIN", "Kablo TV", "Tivibu", "Exxen", "BluTV", "TOD"] },
  { id: "mobile", label: "Mobil Hat", groups: ["mobile"] },
  { id: "digital", label: "Dijital Üyelik", groups: ["digital"] },
  { id: "gym", label: "Spor Salonu", names: ["Spor salonu"] },
  { id: "education", label: "Eğitim / Kurs", names: ["Online eğitim / kurs"] },
  { id: "insurance", label: "Sigorta / BES", names: ["Sigorta / ek paket"] },
  { id: "other", label: "Diğer", names: ["Diğer"] },
];

export function filterCompanyGroups(groups, categoryId) {
  if (!categoryId) return groups;
  const category = COMPANY_CATEGORIES.find((item) => item.id === categoryId);
  if (!category) return groups;
  return groups.map((group) => ({
    ...group,
    items: group.items.filter((name) => {
      const included = category.names?.includes(name) || category.groups?.includes(group.id);
      return included && !category.exclude?.includes(name);
    }),
  })).filter((group) => group.items.length > 0);
}
